import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { Box, Breadcrumbs, Link, Stack, Tab, Tabs, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { endpoints, qk } from '../../api/endpoints';
import { ErrorState, HealthDot, SkeletonRows, StatusChip } from '../../components/common';
import { CloneOverviewTab } from './OverviewTab';
import { AnalyticsTab } from './AnalyticsTab';
import { LogsTab } from './LogsTab';
import { JobsTab } from './JobsTab';
import { CloneSettingsTab } from './SettingsTab';
import { KeysTab } from './KeysTab';
import { TenantTab } from './TenantTab';
import { BrandTab } from './BrandTab';

const TABS = [
  ['overview', 'Overview'],
  ['analytics', 'Analytics'],
  ['logs', 'Logs'],
  ['jobs', 'Jobs'],
  ['settings', 'Settings'],
  ['keys', 'Keys'],
  ['tenant', 'Tenant'],
  ['apps', 'Mobile apps'],
] as const;
export type CloneTab = (typeof TABS)[number][0];

export function ClonePage() {
  const params = useParams();
  const id = Number(params.id);
  const tab = (TABS.find(([k]) => k === params.tab)?.[0] ?? 'overview') as CloneTab;
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: qk.clone(id),
    queryFn: () => endpoints.clone(id),
    refetchInterval: (qq) => (qq.state.data?.status === 'provisioning' || qq.state.data?.status === 'deleting' ? 5000 : 30_000),
  });

  if (q.isLoading) return <SkeletonRows rows={6} height={44} />;
  if (q.isError || !q.data) return <ErrorState error={q.error} onRetry={() => void q.refetch()} title="Could not load the clone" />;
  const c = q.data;

  return (
    <>
      <Breadcrumbs sx={{ mb: 1 }}>
        <Link component={RouterLink} to="/clones" underline="hover" color="text.secondary" variant="body2">
          Clones
        </Link>
        <Typography variant="body2" color="text.secondary">
          {c.slug}
        </Typography>
      </Breadcrumbs>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 2 }} alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
          <HealthDot health={c.health} size={14} />
          <Typography variant="h4" component="h1" noWrap>
            {c.name}
          </Typography>
          <StatusChip status={c.status} />
        </Stack>
        <Typography variant="body2" color="text.secondary" component="div" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          <span>{c.clientName}</span>
          <span>·</span>
          <Link href={`https://${c.domain}`} target="_blank" rel="noreferrer" underline="hover" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            {c.domain} <OpenInNewIcon sx={{ fontSize: 14 }} />
          </Link>
          {c.release && (
            <>
              <span>·</span>
              <span>release {c.release}</span>
            </>
          )}
        </Typography>
      </Stack>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => navigate(`/clones/${id}${v === 'overview' ? '' : `/${v}`}`)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
          {TABS.map(([k, label]) => (
            <Tab key={k} value={k} label={label} />
          ))}
        </Tabs>
      </Box>
      {tab === 'overview' && <CloneOverviewTab clone={c} />}
      {tab === 'analytics' && <AnalyticsTab clone={c} />}
      {tab === 'logs' && <LogsTab clone={c} />}
      {tab === 'jobs' && <JobsTab clone={c} />}
      {tab === 'settings' && <CloneSettingsTab clone={c} />}
      {tab === 'keys' && <KeysTab clone={c} />}
      {tab === 'tenant' && <TenantTab clone={c} />}
      {tab === 'apps' && <BrandTab clone={c} />}
    </>
  );
}
